import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';

export interface IFreightHandlingEntityAttributes extends IModelAttributes {
	label: string;
	colour: string;
	abbreviation: string;
	showInCheckIn: boolean;
	disabled: boolean;

	freightDetails: Array<Models.FreightDetailFreightHandling | Models.IFreightDetailFreightHandlingAttributes>;
	freightPriceRanges: Array<Models.FreightPriceRangeEntity | Models.IFreightPriceRangeEntityAttributes>;
}
@entity('FreightHandlingEntity', 'Freight Handling')
export default class FreightHandlingEntity extends Model implements IFreightHandlingEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * The display label for the freight handling.
	 */
	@observable
	@attribute<FreightHandlingEntity, string>()
	public label: string;

	/**
	 * The colour of the tag for the freight handling.
	 */
	@observable
	@attribute<FreightHandlingEntity, string>()
	public colour: string;

	/**
	 * The abbreviation for the freight handling label.
	 */
	@observable
	@attribute<FreightHandlingEntity, string>()
	public abbreviation: string;

	/**
	 * Whether or not to show the freight handling tag in check-in.
	 */
	@observable
	@attribute<FreightHandlingEntity, boolean>()
	public showInCheckIn: boolean;

	/**
	 * Whether or not this fright handling option is disabled.
	 */
	@observable
	@attribute<FreightHandlingEntity, boolean>()
	public disabled: boolean;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public freightDetails: Models.FreightDetailFreightHandling[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public freightPriceRanges: Models.FreightPriceRangeEntity[] = [];

	constructor(attributes?: Partial<IFreightHandlingEntityAttributes>) {
		super(attributes);

		this.freightDetails = [];
		this.freightPriceRanges = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IFreightHandlingEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.label !== undefined) {
				this.label = attributes.label;
			}
			if (attributes.colour !== undefined) {
				this.colour = attributes.colour;
			}
			if (attributes.abbreviation !== undefined) {
				this.abbreviation = attributes.abbreviation;
			}
			if (attributes.showInCheckIn !== undefined) {
				this.showInCheckIn = attributes.showInCheckIn;
			}
			if (attributes.disabled !== undefined) {
				this.disabled = attributes.disabled;
			}
			if (attributes.freightDetails !== undefined && Array.isArray(attributes.freightDetails)) {
				if (!this.freightDetails) {
					this.freightDetails = [];
				}
				for (const model of attributes.freightDetails) {
					if (model instanceof Models.FreightDetailFreightHandling) {
						this.freightDetails.push(model);
					} else {
						this.freightDetails.push(new Models.FreightDetailFreightHandling(model));
					}
				}
			}
			if (attributes.freightPriceRanges !== undefined && Array.isArray(attributes.freightPriceRanges)) {
				if (!this.freightPriceRanges) {
					this.freightPriceRanges = [];
				}
				for (const model of attributes.freightPriceRanges) {
					if (model instanceof Models.FreightPriceRangeEntity) {
						this.freightPriceRanges.push(model);
					} else {
						this.freightPriceRanges.push(new Models.FreightPriceRangeEntity(model));
					}
				}
			}
		}
	}
}
